import { useEffect, useState } from "react";
import {
  Users, Plus, Trash2, Edit3, Loader2, X, Save, ShieldCheck, UserCog, Phone,
  TrendingUp, Calendar, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import apiClient from "./api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const ROLE_COLOR = {
  admin: "bg-[#FC5106]/15 text-[#FC5106] border-[#FC5106]/40",
  staff: "bg-[#8B73FF]/15 text-[#C9BCFF] border-[#8B73FF]/40",
  sales: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

const ROLE_DESC = {
  admin: "Full access — all data, users, settings, reports",
  staff: "View leads, update status & notes, mark complete",
  sales: "Add new bookings & reminders. Phone numbers visible only to creator + admin.",
};

const inputCls =
  "w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none transition-all";

export default function AdminTeam() {
  const [users, setUsers] = useState([]);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode:'create'|'edit', user?}
  const [windowDays, setWindowDays] = useState(7);

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, teamRes] = await Promise.all([
        apiClient.get("/api/admin/users"),
        apiClient.get(`/api/admin/analytics/team?days=${windowDays}`),
      ]);
      setUsers(usersRes.data);
      setTeam(teamRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [windowDays]);

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})?`)) return;
    try {
      await apiClient.delete(`/api/admin/users/${u.id}`);
      setUsers((arr) => arr.filter((x) => x.id !== u.id));
      toast.success("User removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading && !users.length) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 text-[#FC5106] animate-spin" />
      </div>
    );
  }

  const members = team?.members || [];
  const series = team?.daily_series || [];
  const dates = (series[0]?.data || []).map((d) => d.day);
  const maxDaily = Math.max(1, ...series.flatMap((s) => s.data.map((d) => d.count)));

  return (
    <div className="space-y-8" data-testid="admin-team">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-2">Team management</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">Employees</h1>
          <p className="text-white/50 text-sm mt-1">{users.length} member{users.length !== 1 ? "s" : ""}</p>
        </div>
        <button
          onClick={() => setModal({ mode: "create" })}
          data-testid="add-employee-button"
          className="btn-primary !py-2.5 !px-4 !text-sm"
        >
          <Plus className="w-4 h-4" /> Add employee
        </button>
      </div>

      {/* Member cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((u) => {
          const perf = members.find((m) => m.email?.toLowerCase() === u.email.toLowerCase()) || {};
          return (
            <div
              key={u.id}
              data-testid={`employee-card-${u.id}`}
              className="rounded-2xl border border-white/10 bg-[#121212] p-5 hover:border-white/20 transition"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <h3 className="font-heading text-lg font-bold text-white truncate">{u.name}</h3>
                  <p className="text-white/50 text-xs truncate">{u.email}</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setModal({ mode: "edit", user: u })}
                    className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white/70"
                    aria-label="Edit"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(u)}
                    className="p-1.5 rounded-full bg-white/5 hover:bg-red-500/15 hover:text-red-300 text-white/70"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${ROLE_COLOR[u.role] || ROLE_COLOR.staff}`}>
                  {u.role}
                </span>
                {!u.active && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/50">
                    Inactive
                  </span>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/5">
                <Stat label="Today" value={perf.bookings_today ?? 0} />
                <Stat label="Week" value={perf.bookings_this_week ?? 0} />
                <Stat label="Month" value={perf.bookings_this_month ?? 0} />
              </div>
              <p className="text-white/40 text-[10px] mt-3 text-center">
                Total leads: <span className="text-white/70 font-semibold">{perf.leads_total ?? 0}</span>
              </p>
            </div>
          );
        })}
      </div>

      {/* Team performance section */}
      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="team-tracker">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-white">Team tracker</h2>
            <p className="text-white/50 text-xs">Bookings/leads added per employee per day</p>
          </div>
          <div className="w-44">
            <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
              <SelectTrigger className="bg-black/50 border-white/10 text-sm text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white">
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {series.length === 0 ? (
          <p className="text-white/40 text-sm py-12 text-center">No team activity yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="team-tracker-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-white/50">
                  <th className="px-3 py-2 text-left font-medium sticky left-0 bg-[#121212]">Employee</th>
                  {dates.map((d) => (
                    <th key={d} className="px-2 py-2 text-center font-medium whitespace-nowrap">
                      {d.slice(5)}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {series.map((s) => {
                  const total = s.data.reduce((a, b) => a + b.count, 0);
                  return (
                    <tr key={s.user_email} className="border-t border-white/5">
                      <td className="px-3 py-2.5 text-white font-semibold sticky left-0 bg-[#121212]">
                        {s.user_name}
                        <p className="text-[10px] text-white/40 font-normal">{s.user_email}</p>
                      </td>
                      {s.data.map((d) => (
                        <td key={d.day} className="px-2 py-2 text-center">
                          <span
                            className={`inline-flex items-center justify-center min-w-[28px] h-6 rounded text-xs font-semibold ${
                              d.count === 0 ? "text-white/50" : "text-white"
                            }`}
                            style={
                              d.count > 0
                                ? {
                                    background: `rgba(252, 81, 6, ${Math.min(0.15 + (d.count / maxDaily) * 0.65, 0.85)})`,
                                  }
                                : {}
                            }
                          >
                            {d.count || "·"}
                          </span>
                        </td>
                      ))}
                      <td className="px-3 py-2.5 text-right">
                        <span className="font-heading font-bold text-white">{total}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <UserModal
          mode={modal.mode}
          user={modal.user}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <p className="font-heading font-bold text-white text-lg">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-white/40">{label}</p>
    </div>
  );
}

function UserModal({ mode, user, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    email: user?.email || "",
    name: user?.name || "",
    role: user?.role || "sales",
    password: "",
    active: user?.active ?? true,
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.email || !form.name) {
      toast.error("Email and name are required");
      return;
    }
    if (!isEdit && !form.password) {
      toast.error("Password is required for new employee");
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await apiClient.patch(`/api/admin/users/${user.id}`, payload);
        toast.success("Employee updated");
      } else {
        await apiClient.post("/api/admin/users", form);
        toast.success("Employee added");
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="user-modal"
    >
      <div className="w-full max-w-md rounded-2xl bg-[#0d0d0d] border border-white/10 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="bg-[#FC5106]/15 border border-[#FC5106]/40 w-10 h-10 rounded-xl flex items-center justify-center">
              <UserCog className="w-5 h-5 text-[#FC5106]" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#FC5106]">{isEdit ? "Edit" : "Add"}</p>
              <h2 className="font-heading text-lg font-bold text-white">
                {isEdit ? user.name : "New employee"}
              </h2>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white" data-testid="user-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Name *</label>
            <input
              data-testid="user-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className={inputCls}
              placeholder="e.g. Suman Kar"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Email *</label>
            <input
              data-testid="user-email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              className={inputCls}
              placeholder="suman@barknfurs.com"
              disabled={isEdit}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">Role *</label>
            <Select value={form.role} onValueChange={(v) => update("role", v)}>
              <SelectTrigger data-testid="user-role" className={inputCls + " text-left"}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white z-[90]">
                <SelectItem value="admin">Admin (full access)</SelectItem>
                <SelectItem value="staff">Staff (groomer)</SelectItem>
                <SelectItem value="sales">Sales (entry only)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-white/40 mt-2 leading-snug">{ROLE_DESC[form.role]}</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 mb-1.5 block">
              {isEdit ? "Reset password (optional)" : "Password *"}
            </label>
            <input
              data-testid="user-password"
              type="password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              className={inputCls}
              placeholder={isEdit ? "Leave blank to keep current" : "Min 4 characters"}
            />
          </div>
          {isEdit && (
            <label className="flex items-center gap-3 text-sm text-white/80 cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => update("active", e.target.checked)}
                className="w-4 h-4 rounded accent-[#FC5106]"
                data-testid="user-active"
              />
              Account active
            </label>
          )}

          <button
            onClick={submit}
            disabled={submitting}
            data-testid="user-submit"
            className="btn-primary w-full justify-center disabled:opacity-50"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : (isEdit ? "Save changes" : "Add employee")}
          </button>
        </div>
      </div>
    </div>
  );
}
